/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import SideNavBottomSlot from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavBottomSlot';
import {
  SideNavCollapseProvider,
  useSideNavCollapse,
} from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';
import SideNavHeader from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavHeader';
import SideNavList from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavList';
import type { SideNavItem, SideNavWidths } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import { ADMIN_SIDENAV_TRANSITION_MS } from 'bundles/enterprise-admin-navigation/constants';

const styles = {
  nav: css`
    width: var(--sidenav-width);
    transition: width ${ADMIN_SIDENAV_TRANSITION_MS}ms ease;
    height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: var(--cds-color-neutral-background-primary);
    border-right: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  listContainer: css`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  `,
};

type Props = {
  ariaLabel: string;
  widths: SideNavWidths;
  logo?: React.ReactNode;
  items: SideNavItem[];
  bottom?: React.ReactNode;
};

/**
 *
 * The SideNav component is a wrapper around the SideNavHeader, SideNavList, and SideNavBottomSlot components.
 * It provides a consistent layout for the sidebar.
 *
 * @param ariaLabel - The aria-label for the sidenav
 * @param widths - The widths of the sidenav
 * @param logo - The logo for the sidenav
 * @param items - The navigation items for the sidenav
 * @param bottom - The component to render in the bottom slot for the sidenav
 * @returns The SideNav component
 */
const SideNavContent = ({ ariaLabel, widths, logo, items, bottom }: Props) => {
  const { isSideNavCollapsed } = useSideNavCollapse();
  const widthPx = isSideNavCollapsed ? widths.collapsedPx : widths.expandedPx;

  return (
    <nav aria-label={ariaLabel} css={styles.nav} style={{ '--sidenav-width': `${widthPx}px` } as React.CSSProperties}>
      <SideNavHeader logo={logo} />
      <div css={styles.listContainer}>
        <SideNavList items={items} />
      </div>
      {bottom && <SideNavBottomSlot>{bottom}</SideNavBottomSlot>}
    </nav>
  );
};

export const SideNav = (props: Props) => {
  return (
    <SideNavCollapseProvider>
      <SideNavContent {...props} />
    </SideNavCollapseProvider>
  );
};
